<template>
  <div
    :id="`subcategory_goal_${id}`"
    :data-id="id"
    class="subcategory-goal relative outline-none focus-within:ring bg-white card flex flex-col divide-y divide-gray-100 text-sm flex-shrink-0"
    :class="[
      isEnabled ? 'opacity-100' : 'opacity-50',
      { 'border-l-6': isEnabled && categoryColor }
    ]"
    :style="isEnabled && categoryColor ? `border-left-color: ${categoryColor}` : ''"
    tabindex="0"
    @click="handleClick"
    @mouseenter="showToolbar = true"
    @mouseleave="showToolbar = false"
  >
    <section class="goal-header flex-grow">
      <h3
        class="text-gray-800 font-medium text-base mb-2 truncate"
        style="min-height: 24px;"
        :title="title"
      >
        {{ title }}
      </h3>
      <p
        v-if="subCategoryDescriptionComputed !== undefined && subCategoryDescriptionComputed !== null"
        class="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-2 whitespace-pre-line"
        :title="subCategoryDescriptionComputed"
      >
        {{ subCategoryDescriptionComputed }}
      </p>
      <p
        v-if="goalDescriptionComputed"
        class="text-gray-600 text-sm leading-relaxed line-clamp-2 whitespace-pre-line"
        :title="goalDescriptionComputed"
      >
        <span class="font-bold">{{ $t('Shared.goal_label') }}: </span>{{ goalDescriptionComputed }}
      </p>
    </section>

    <!-- RATING TAGS -->
    <section
      class="goal-ratings flex gap-2 flex-wrap pt-3 relative"
    >
      <div v-if="showRating && goals && goals.length > 0" class="flex gap-1">
        <BaseTag
          v-for="goal in goals"
          :key="`goal_rating_${goal.id}`"
          :id="`goal_rating_tag_${goal.id}`"
          :bg-color="showMultipleRatings ? isSelectedGoal(goal) ? goal.goalCollectionColor : 'transparent' : 'gray-500'"
          :class="showMultipleRatings ? `border-2 border-${goal.goalCollectionColor}` : ''"
          :text-color="showMultipleRatings && !isSelectedGoal(goal) ? goal.goalCollectionColor : 'white'"
          :label="`${goal.rating >= 0 ? '+' : ''}${goal.rating}`"
          @click.stop="handleRatingClick"
        />
      </div>
          <!-- ENABLED TOGGLE -->
      <div
        v-if="canToggle"
        class="ml-auto"
      >
        <BaseToggle
          :modelValue="isEnabled"
          @update:modelValue="handleToggleEnabled"
          size="sm"
          data-cy="subcategory-goal-enabled-toggle"
          :checkedBgColor="'green'"
          @click.stop
        />
      </div>
    </section>

    <!-- TOOLBAR -->
    <div
      v-show="showToolbar"
      class="absolute top-1 right-1 p-1 bg-gray-100 rounded flex gap-1"
    >
      <BaseButton
        v-if="canEdit"
        :id="`editButton_goal_${id}`"
        :title="$t('Shared.edit')"
        layout="icon-only"
        size="sm"
        icon="BaseEdit"
        @click.stop="handleEditGoal"
      />
      <BaseButton
        v-if="canDelete"
        :id="`deleteButton_goal_${id}`"
        :title="$t('Shared.delete')"
        layout="icon-only"
        size="sm"
        icon="BaseTrash"
        :color="'red-500'"
        @click.stop="handleDeleteGoal"
      />
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/shared/BaseButton'
import BaseTag from '@/components/shared/BaseTag'
import BaseToggle from '@/components/shared/BaseToggle'
import { getCollectionColor } from '@/composables/goalCollections/utils'

export default {
  name: 'SubCategoryGoalCard',
  components: {
    BaseButton,
    BaseTag,
    BaseToggle,
  },
  props: {
    subCategoryId: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    subCategoryDescription: {
      type: String,
      required: false,
      default: '',
    },
    goalDescription: {
      type: String,
      required: false,
      default: '',
    },
    rating: {
      type: Number,
      required: false,
      default: 0,
    },
    isEnabled: {
      type: Boolean,
      default: false,
    },
    goalCollectionsSubCategories: {
      type: Array,
      required: false,
      default: () => [],
    },
    selectedCollectionId: {
      type: Number,
      required: true,
    },
    showRating: {
      type: Boolean,
      default: true,
    },
    showMultipleRatings: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    categoryColor: {
      type: String,
      default: '#6b7280',
    },
    canToggle: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['editGoal', 'deleteGoal', 'clicked', 'toggleEnabled'],
  data () {
    return {
      showToolbar: false,
    }
  },
  methods: {
    handleClick () {
      this.$emit('clicked')
    },
    handleEditGoal () {
      this.$emit('editGoal')
    },
    handleRatingClick () {
      if (this.canEdit) {
        this.$emit('editGoal')
      }
    },
    handleDeleteGoal () {
      this.$emit('deleteGoal')
    },
    handleToggleEnabled (isEnabled) {
      this.$emit('toggleEnabled', isEnabled)
    },
    isSelectedGoal (goal) {
      return this.showMultipleRatings && goal.goalCollectionId === this.selectedCollectionId
    },
  },
  computed: {
    id () {
      return this.subCategoryId || null
    },
    goals () {
      if (this.showRating && !this.showMultipleRatings) {
        return [{
          id: this.subCategoryId + '_goal_rating',
          goalCollectionId: this.selectedCollectionId,
          goalCollectionColor: getCollectionColor(this.selectedCollectionId),
          rating: this.rating,
        }]
      }
      return this.goalCollectionsSubCategories || []
    },
    selectedGoal () {
      if (!this.goalCollectionsSubCategories || !this.goalCollectionsSubCategories.length) return null
      return this.goals.find(goal => goal.goalCollectionId === this.selectedCollectionId)
    },
    subCategoryDescriptionComputed () {
      return this.selectedGoal ? this.selectedGoal.subCategoryDescription : this.subCategoryDescription
    },
    goalDescriptionComputed () {
      return this.selectedGoal ? this.selectedGoal.description : this.goalDescription
    },
  },
}
</script>

<style scoped>
.subcategory-goal {
  touch-action: none;
  user-select: none;
  min-width: 16rem;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
